import { G } from '@mobily/ts-belt';
import { pick } from 'lodash';
import { DataProvider } from 'react-admin';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { PostCalculateMatchingContributionsEndpoint } from '@Common/types/bo/b2b/campaigns';

import { handleCampaignTransform } from '@sh4res/bo-common/components/B2bCampaigns/transform';
import { ResourceBasePath } from '@sh4res/bo-common/hooks';

/**
 * Holds the result of the last ceiling check so the review step can list the employees. Not part
 * of the campaign payload: handleCampaignTransform only keeps whitelisted keys.
 */
export const MATCHING_CONTRIBUTION_CEILING_OVERRUNS_FIELD =
  'matching_contribution_ceiling_overruns';

export type MatchingContributionCeilingOverrunRow = {
  identifier: string;
  firstname: string | null;
  lastname: string | null;
  savingPlanCategory: string;
  exceededBy: number;
};

/** Subset of AllocationRecord this check reads, kept local to avoid an import cycle. */
type AllocationForCeilingCheck = {
  employee_id?: number | null;
  identifier?: string;
  firstname?: string | null;
  lastname?: string | null;
  company_collected_choices?: { matching_contribution_amount?: number | null }[];
};

const hasMatchingContributionAmount = (allocations: AllocationForCeilingCheck[]): boolean =>
  allocations.some((alloc) =>
    (alloc.company_collected_choices ?? []).some(
      (choice) => (choice.matching_contribution_amount ?? 0) > 0,
    ),
  );

/**
 * Checks every allocation at once against the matching contribution ceilings, so a file upload is
 * validated without expanding a single row. The backend rejects the campaign on the same grounds
 * when it is created, this only reports it early and per employee.
 *
 * A failing check reports nothing rather than throwing: the step validators run outside any error
 * boundary, and the campaign creation stays guarded server-side either way.
 */
export const getMatchingContributionCeilingOverruns = async (
  form: UseFormReturn<FieldValues>,
  dataProvider: DataProvider,
  resourceBasePath: ResourceBasePath,
): Promise<MatchingContributionCeilingOverrunRow[]> => {
  try {
    return await computeMatchingContributionCeilingOverruns(form, dataProvider, resourceBasePath);
  } catch (error) {
    // oxlint-disable-next-line no-console
    // eslint-disable-next-line no-console -- error useful for debugging
    console.error(error);
    return [];
  }
};

const computeMatchingContributionCeilingOverruns = async (
  form: UseFormReturn<FieldValues>,
  dataProvider: DataProvider,
  resourceBasePath: ResourceBasePath,
): Promise<MatchingContributionCeilingOverrunRow[]> => {
  const formValues = form.getValues();
  const allocations = (formValues.allocations ?? []) as AllocationForCeilingCheck[];

  if (
    formValues.matching_contribution_computed_by !== 'company' ||
    !hasMatchingContributionAmount(allocations)
  ) {
    return [];
  }

  const payload = await handleCampaignTransform({
    isDraft: true,
    action: 'create',
    includeEmptyMatchingContributionChoices: true,
  })(formValues);

  const { data: response } = await dataProvider.create<
    PostCalculateMatchingContributionsEndpoint['Client']['ResponseBody']
  >(`${resourceBasePath}campaigns/calculate_matching_contributions`, {
    data: {
      ...pick(payload, 'company_id', 'b2b_contract_ids', 'type'),
      allocations:
        payload.allocations as PostCalculateMatchingContributionsEndpoint['Client']['RequestBody']['allocations'],
      amount_type_input: formValues.amount_type_input,
      matching_contribution_computed_by: 'company',
      matching_contribution_related_cash_source:
        formValues.matching_contribution_related_cash_source,
      reason: formValues.reason,
      fiscal_period_start: formValues.fiscal_period_start,
      reference_year: formValues.reference_year ?? null,
    } satisfies PostCalculateMatchingContributionsEndpoint['Client']['RequestBody'],
  });

  const allocationsByEmployeeId = new Map(
    allocations
      .filter((alloc) => G.isNotNullable(alloc.employee_id))
      .map((alloc) => [alloc.employee_id, alloc]),
  );

  return response.ceiling_overruns.map((overrun) => {
    const allocation = allocationsByEmployeeId.get(overrun.employee_id);

    return {
      identifier: allocation?.identifier ?? `#${overrun.employee_id}`,
      firstname: allocation?.firstname ?? null,
      lastname: allocation?.lastname ?? null,
      savingPlanCategory: overrun.saving_plan_category,
      exceededBy: overrun.exceeded_by,
    };
  });
};
